import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/layout';
import { HelpCircle, Mail, Clock, ShieldAlert, Bot, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSupportTranslation } from './support-translations';

export const SupportPage: React.FC = () => {
  const { direction } = useLanguage();
  const t = useSupportTranslation();

  return (
    <div
      data-testid="support-page"
      dir={direction}
      className="min-h-screen flex flex-col justify-between bg-slate-50 dark:bg-slate-950 transition-colors"
    >
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8 space-y-8">
          <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950/60 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <HelpCircle className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t.title}
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t.subtitle}
              </p>
            </div>
          </div>

          <section aria-labelledby="support-contact-heading" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/60 rounded-xl space-y-2">
              <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100 font-semibold text-sm">
                <Mail className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <h2 id="support-contact-heading">{t.contactHeader}</h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {t.contactEmailDesc}
              </p>
              <a
                href={`mailto:${t.contactEmail}`}
                className="inline-block text-sm font-semibold text-emerald-600 dark:text-emerald-400 hover:underline pt-1"
              >
                {t.contactEmail}
              </a>
            </div>

            <div className="p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/60 rounded-xl space-y-2">
              <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100 font-semibold text-sm">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <h2>{t.responseTime}</h2>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {t.responseDesc}
              </p>
            </div>
          </section>

          <section aria-labelledby="support-faq-heading" className="space-y-4">
            <div className="border-b border-slate-200 dark:border-slate-800 pb-2">
              <h2
                id="support-faq-heading"
                className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2"
              >
                <MessageSquare className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                {t.faqHeader}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t.faqSubtitle}
              </p>
            </div>
            <div className="space-y-4">
              {t.faqs.map((faq, index) => (
                <div
                  key={index}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 space-y-1.5"
                >
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {faq.question}
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section
            aria-labelledby="other-channels-heading"
            className="border-t border-slate-200 dark:border-slate-800 pt-6 space-y-4"
          >
            <div>
              <h2
                id="other-channels-heading"
                className="text-base font-semibold text-slate-900 dark:text-slate-100"
              >
                {t.channelsHeader}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <ShieldAlert className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                  <h3>{t.delistHeader}</h3>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  {t.delistDesc}
                </p>
                <Link
                  to="/abuse"
                  className="inline-block text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline"
                >
                  {t.delistLink}
                </Link>
              </div>

              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                  <Bot className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <h3>{t.crawlerHeader}</h3>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  {t.crawlerDesc}
                </p>
                <Link
                  to="/bot"
                  className="inline-block text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  {t.crawlerLink}
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};
